import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase.Config';
import toast from 'react-hot-toast';

const saveUserToDatabase = async (user) => {
    await setDoc(doc(db, 'users', user.uid), {
        uid:       user.uid,
        name:      user.displayName || '',
        email:     user.email,
        photoURL:  user.photoURL || '',
        provider:  user.providerData[0]?.providerId || 'email',
        createdAt: serverTimestamp(),
    }, { merge: true });
};

const SignUpForm = ({ handleResponse }) => {
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors } } = useForm();

    const onSubmit = async ({ name, email, password }) => {
        const loading = toast.loading('Creating account...');
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);
            await updateProfile(result.user, { displayName: name });
            await saveUserToDatabase({ ...result.user, displayName: name });
            toast.dismiss(loading);
            toast.success('Account created successfully!');
            handleResponse(result.user);
            navigate('/');
        } catch (error) {
            toast.dismiss(loading);
            if (error.code === 'auth/email-already-in-use') {
                toast.error('This email is already registered.');
            } else if (error.code === 'auth/weak-password') {
                toast.error('Password must be at least 6 characters.');
            } else {
                toast.error(error.message);
            }
        }
    };

    const handleGoogleSignUp = async () => {
        const loading = toast.loading('Signing in with Google...');
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            await saveUserToDatabase(result.user);
            toast.dismiss(loading);

            // ── success popup ──────────────────────────────────────────────
            toast.success(`🎉 Signed in successfully! Welcome, ${result.user.displayName}!`, {
                duration: 4000,
                style: {
                    borderRadius: '12px',
                    background: '#fff',
                    color: '#333',
                    fontWeight: '500',
                    boxShadow: '0 4px 24px rgba(115,85,247,0.18)',
                },
                iconTheme: {
                    primary: '#7355F7',
                    secondary: '#fff',
                },
            });

            handleResponse(result.user);
            navigate('/');
        } catch (error) {
            toast.dismiss(loading);
            if (error.code !== 'auth/popup-closed-by-user') {
                toast.error(error.message);
            }
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="sign-up-form">
            <h2 className="title">Sign Up</h2>

            <div className="input-field">
                <span className="fIcon"><FontAwesomeIcon icon={faUser} /></span>
                <input
                    type="text"
                    placeholder="Name"
                    {...register("name", { required: true })}
                />
            </div>
            {errors.name && <span className="text-warning">Name is required</span>}

            <div className="input-field">
                <span className="fIcon"><FontAwesomeIcon icon={faEnvelope} /></span>
                <input
                    type="email"
                    placeholder="Email"
                    {...register("email", { required: true })}
                />
            </div>
            {errors.email && <span className="text-warning">Email is required</span>}

            <div className="input-field">
                <span className="fIcon"><FontAwesomeIcon icon={faLock} /></span>
                <input
                    type="password"
                    placeholder="Password"
                    {...register("password", { required: true, minLength: 6 })}
                />
            </div>
            {errors.password && <span className="text-warning">Password must be at least 6 characters</span>}

            <input className="iBtn" type="submit" value="Sign Up" />

            <p className="social-text">Or sign up with social account</p>

            <div className="social-media">
                <button
                    type="button"
                    className="social-icon"
                    onClick={handleGoogleSignUp}
                    title="Sign up with Google"
                >
                    <svg width="20" height="20" viewBox="0 0 48 48">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                </button>
            </div>
        </form>
    );
};

export default SignUpForm;